import React, { useEffect, useRef, memo } from 'react';
import { createSelector } from 'reselect';
import { useSelector } from 'react-redux';
import MlbNativeWrapper from '@gameday/utils/MlbNativeWrapper';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';
import { getGameStaticData } from '@gameday/utils/selectors/getGameStaticData';

const getLocalTimezone = (state) => state.config?.localTimezone;

const getSyncData = createSelector(
    [
        getGameStaticData,
        getLocalTimezone
    ],
    (
        gameStaticData,
        localTimezone = et
    ) => {
        const {
            pk: gamePk,
            datetime: {
                dateTime: date
            } = {}
        } = gameStaticData;
        return ({
            gamePk,
            date,
            tz: localTimezone
        });
    }
);

const et = 'America/New_York';

const formatDate = (date, tz) => {
    const UTCDate = zonedTimeToUtc(date, tz);
    const formattedDate = format(utcToZonedTime(UTCDate, et), 'yyyy-MM-dd');
    return formattedDate;
};

const GamePkSync = () => {
    const {
        gamePk,
        date,
        tz
    } = useSelector(getSyncData);

    const gameCache = useRef({ gamePk, date });

    const mlbNativeJsBridge = useRef(null);

    MlbNativeWrapper.then((results) => {
        const { updateGame } = results;
        mlbNativeJsBridge.current = {
            updateGame
        };
    });

    useEffect(() => {
        const { gamePk: cachedGamePk, date: cachedDate } = gameCache?.current;
        // Confirm we have a gamePk and date,
        // and that they are different than what we have cached.
        // Do not dispatch on the initial gamePk/date set.
        if (
            (gamePk && date) &&
            (
                (cachedGamePk && cachedGamePk !== gamePk) ||
                (cachedDate && cachedDate !== date)
            )
        ) {
            const tvDate = formatDate(date, tz);
            gameCache.current = { gamePk, date };
            mlbNativeJsBridge?.current?.updateGame?.(gamePk, tvDate);
        }
    }, [gamePk, date]);

    return null;
};

const memoed = memo(GamePkSync);

memoed.displayName = 'GamePkSync';

export default memoed;
